import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MemberPageScaffold, MemberPill } from "@/components/MemberScaffold";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { formatPublicLocation } from "@/lib/locationPrivacy";
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppEmptyState,
  AppInlineAlert,
  AppLoadingState,
  AppSection,
  AppSectionHeader,
  AppSegmentedControl,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";
import {
  DISCOVERY_CHILD_AGE_LABELS,
  DISCOVERY_CHILD_AGE_OPTIONS,
  FUTURE_MEMBER_VERIFICATION,
  PROFILE_AVAILABILITY_OPTIONS,
  PROFILE_INTEREST_LABELS,
  PROFILE_INTEREST_OPTIONS,
  getDiscoveryAgeBands,
  matchesMemberDiscoveryFilters,
  type DiscoveryChildAgeValue,
  type ProfileAvailabilityValue,
  type ProfileInterestValue,
} from "../../shared/community";
import { orderMembersByLocality } from "../../shared/location";

type MemberProfileRow = Pick<
  Tables<"profiles">,
  "id" | "display_name" | "postcode" | "country_code" | "avatar_url" | "interests" | "availability" | "custody_arrangement"
> & {
  child_age_bands: string[];
};
type ConnectionRow = Tables<"connections">;

const LocalMembers = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [members, setMembers] = useState<MemberProfileRow[]>([]);
  const [connections, setConnections] = useState<ConnectionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [childAgeFilter, setChildAgeFilter] = useState<DiscoveryChildAgeValue | "all">("all");
  const [interestFilters, setInterestFilters] = useState<ProfileInterestValue[]>([]);
  const [availabilityFilters, setAvailabilityFilters] = useState<ProfileAvailabilityValue[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        setMembers([]);
        setConnections([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      const [{ data: profiles }, { data: nextConnections }, { data: childAgeBands }, { data: viewerProfile }] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, display_name, postcode, country_code, avatar_url, interests, availability, custody_arrangement")
          .neq("id", user.id)
          .order("display_name", { ascending: true })
          .limit(50),
        supabase.from("connections").select("*").or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`),
        supabase.from("member_child_age_bands").select("user_id, age_bands"),
        supabase.from("my_private_profile").select("postcode, country_code").maybeSingle(),
      ]);

      const childAgeBandsByUserId = new Map<string, string[]>();
      for (const row of childAgeBands ?? []) {
        if (!row.user_id) continue;
        childAgeBandsByUserId.set(row.user_id, row.age_bands ?? []);
      }

      setMembers(
        orderMembersByLocality(((profiles ?? []) as Omit<MemberProfileRow, "child_age_bands">[]).map((profile) => ({
          ...profile,
          child_age_bands: childAgeBandsByUserId.get(profile.id) ?? [],
        })), viewerProfile ?? null),
      );
      setConnections((nextConnections ?? []) as ConnectionRow[]);
      setLoading(false);
    };

    void fetchData();
  }, [user]);

  const filteredMembers = useMemo(
    () =>
      members.filter((member) =>
        matchesMemberDiscoveryFilters(member, {
          childAge: childAgeFilter,
          interests: interestFilters,
          availability: availabilityFilters,
        }),
      ),
    [availabilityFilters, childAgeFilter, interestFilters, members],
  );

  const hasActiveFilters =
    childAgeFilter !== "all" || interestFilters.length > 0 || availabilityFilters.length > 0;

  const getConnectionStatus = (memberId: string) => {
    const connection = connections.find(
      (item) => item.requester_id === memberId || item.recipient_id === memberId,
    );

    return connection?.status ?? null;
  };

  const connect = async (memberId: string) => {
    if (!user) return;

    const { error } = await supabase
      .from("connections")
      .insert({ requester_id: user.id, recipient_id: memberId });

    if (error) {
      toast({ title: "Couldn't send request", variant: "destructive" });
      return;
    }

    toast({ title: "Connection request sent" });
    const { data } = await supabase
      .from("connections")
      .select("*")
      .or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`);

    setConnections((data ?? []) as ConnectionRow[]);
  };

  const toggleInterest = (value: ProfileInterestValue) => {
    setInterestFilters((current) =>
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value],
    );
  };

  const toggleAvailability = (value: ProfileAvailabilityValue) => {
    setAvailabilityFilters((current) =>
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value],
    );
  };

  return (
    <MemberPageScaffold
      title="Members nearby"
      subtitle="Single-parent families in Kindred near you. Take your time, filter gently, and only reach out when it feels right."
      fallbackPath="/kinfeed"
    >
      {loading ? (
        <AppLoadingState
          title="Loading nearby members"
          body="Pulling together nearby profiles and connection state."
        />
      ) : members.length === 0 ? (
        <AppEmptyState
          title="No nearby members yet"
          body="As more single-parent families join nearby, they’ll show up here."
        />
      ) : (
        <AppSection gap="$md">
          <AppInlineAlert title="Go at your own pace">
            Browse profiles, take your time with filters, and only send a connection request when someone feels like a comfortable fit.
          </AppInlineAlert>

          <AppCard>
            <AppCardBody gap="$sm">
              <AppSectionHeader
                title="Find a good fit"
                subtitle="Use filters if they help. You never need to rush into reaching out."
              />

              <AppSegmentedControl
                value={childAgeFilter}
                onChange={(value) => setChildAgeFilter(value as DiscoveryChildAgeValue | "all")}
                options={[
                  { value: "all", label: "All ages" },
                  ...DISCOVERY_CHILD_AGE_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
                ]}
              />

              <AppSection gap="$xs">
                <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" textTransform="uppercase" letterSpacing={1.2}>
                  Interests
                </AppText>
                <AppButtonRow>
                  {PROFILE_INTEREST_OPTIONS.map((option) => (
                    <AppButton
                      key={option.value}
                      type="button"
                      size="sm"
                      tone={interestFilters.includes(option.value) ? "primary" : "outline"}
                      onPress={() => toggleInterest(option.value)}
                    >
                      {option.label}
                    </AppButton>
                  ))}
                </AppButtonRow>
              </AppSection>

              <AppSection gap="$xs">
                <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" textTransform="uppercase" letterSpacing={1.2}>
                  Availability
                </AppText>
                <AppButtonRow>
                  {PROFILE_AVAILABILITY_OPTIONS.map((option) => (
                    <AppButton
                      key={option.value}
                      type="button"
                      size="sm"
                      tone={availabilityFilters.includes(option.value) ? "primary" : "outline"}
                      onPress={() => toggleAvailability(option.value)}
                    >
                      {option.label}
                    </AppButton>
                  ))}
                </AppButtonRow>
              </AppSection>
            </AppCardBody>
          </AppCard>

          {filteredMembers.length === 0 ? (
            <AppEmptyState
              title={hasActiveFilters ? "No members match those filters yet" : "No nearby members yet"}
              body={
                hasActiveFilters
                  ? "Try broadening the filters a little and more people should appear."
                  : "As more single-parent families join nearby, they’ll show up here."
              }
            />
          ) : (
            <>
              <AppSectionHeader
                title="Nearby members"
                subtitle={
                  hasActiveFilters
                    ? `${filteredMembers.length} member${filteredMembers.length === 1 ? "" : "s"} feel like a closer fit right now.`
                    : `${filteredMembers.length} member${filteredMembers.length === 1 ? "" : "s"} nearby when you want to start with a hello.`
                }
              />

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {filteredMembers.map((member) => {
                  const status = getConnectionStatus(member.id);
                  const ageBands = getDiscoveryAgeBands(member.child_age_bands);

                  return (
                    <AppCard key={member.id}>
                      <AppCardBody alignItems="center" gap="$sm">
                        <button type="button" onClick={() => navigate(`/community/member/${member.id}`)}>
                          <Avatar className="h-16 w-16">
                            {member.avatar_url ? (
                              <AvatarImage src={member.avatar_url} alt={member.display_name ?? "Member"} />
                            ) : null}
                            <AvatarFallback className="bg-primary/20 text-primary text-xl font-semibold">
                              {(member.display_name ?? "K")[0]}
                            </AvatarFallback>
                          </Avatar>
                        </button>

                        <button
                          type="button"
                          onClick={() => navigate(`/community/member/${member.id}`)}
                          className="w-full"
                        >
                          <AppText
                            fontFamily={kindredFontFamilies.sansBold}
                            textAlign="center"
                          >
                            {member.display_name ?? "Member"}
                          </AppText>
                        </button>

                        {member.postcode ? (
                          <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm" textAlign="center">
                            {formatPublicLocation(member.postcode, member.country_code)}
                          </AppText>
                        ) : null}

                        <div className="flex flex-col items-center gap-2">
                          <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" textAlign="center">
                            {FUTURE_MEMBER_VERIFICATION.title}
                          </AppText>
                          <MemberPill tone="secondary">{FUTURE_MEMBER_VERIFICATION.statusTag}</MemberPill>
                        </div>

                        {member.interests?.length ? (
                          <div className="flex flex-wrap justify-center gap-2">
                            {member.interests.slice(0, 2).map((value) => (
                              <MemberPill key={value} tone="primary">
                                {PROFILE_INTEREST_LABELS[value] ?? value}
                              </MemberPill>
                            ))}
                          </div>
                        ) : null}

                        {ageBands.length ? (
                          <div className="flex flex-wrap justify-center gap-2">
                            {ageBands.map((band) => (
                              <MemberPill key={band} tone="secondary">
                                Ages {DISCOVERY_CHILD_AGE_LABELS[band] ?? band}
                              </MemberPill>
                            ))}
                          </div>
                        ) : null}

                        {status === "connected" ? (
                          <MemberPill tone="success">Connected</MemberPill>
                        ) : status === "pending" ? (
                          <MemberPill tone="neutral">Requested</MemberPill>
                        ) : (
                          <AppButton tone="outline" size="sm" onPress={() => connect(member.id)}>
                            Connect
                          </AppButton>
                        )}
                      </AppCardBody>
                    </AppCard>
                  );
                })}
              </div>
            </>
          )}
        </AppSection>
      )}
    </MemberPageScaffold>
  );
};

export default LocalMembers;
